import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { client } from "../client";

// Per-merchant risk config payload, matching the `saveConfigSchema` bulk endpoint
// in packages/api/src/resources/admin/merchantRisks/routes.ts. A given riskId must
// appear in at most one bucket (enforced server-side).
export type MerchantRiskConfigEntry = {
  riskId: string;
  tier?: number | null;
  config?: Record<string, unknown>;
  autoConfirm?: boolean;
};

export type MerchantSettingsInput = {
  productName?: string | null;
  productDescription?: string | null;
  domainKnowledge?: Record<string, string>;
};

export type SaveMerchantRisksPayload = {
  merchantId: string;
  enabled: MerchantRiskConfigEntry[];
  updated: MerchantRiskConfigEntry[];
  disabled: string[];
  // Saved in the same transaction as the risks, upserted first so risk
  // placeholder validation runs against the new domain knowledge.
  settings?: MerchantSettingsInput;
};

// The catalog of risks visible to this merchant (globals + the merchant's customs).
// GET /admin/risks?merchantId=X (RisksRepo.listForMerchant)
export const useMerchantRiskCatalog = (merchantId: string | undefined) => {
  return useQuery({
    queryKey: ["admin", "risks", "catalog", merchantId],
    queryFn: async () => {
      if (!merchantId) throw new Error("Merchant ID is required");
      const res = await client.admin.risks.$get({ query: { merchantId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!merchantId,
  });
};

// The merchant's current merchant_risks rows (tier + config + joined risk).
// GET /merchant/:merchantId/risks (admins may access any merchant).
export const useMerchantRisks = (merchantId: string | undefined) => {
  return useQuery({
    queryKey: ["admin", "risks", "merchant", merchantId],
    queryFn: async () => {
      if (!merchantId) throw new Error("Merchant ID is required");
      const res = await client.merchant[":merchantId"].risks.$get({ param: { merchantId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!merchantId,
  });
};

// POST /admin/merchant-risks/config — bulk enable/update/disable in one tx.
export const useSaveMerchantRisks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ merchantId, enabled, updated, disabled, settings }: SaveMerchantRisksPayload) => {
      const res = await client.admin["merchant-risks"].config.$post({
        json: { merchantId, enabled, updated, disabled, settings },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "risks", "merchant", merchantId] });
      // Newly-added custom risks now satisfy listForMerchant's visibility check.
      queryClient.invalidateQueries({ queryKey: ["admin", "risks", "catalog", merchantId] });
      // Settings were upserted in the same call.
      queryClient.invalidateQueries({ queryKey: ["admin", "merchant-settings", merchantId] });
    },
  });
};

// GET /merchant/:merchantId/settings — product info, domain knowledge, etc.
export const useMerchantSettings = (merchantId: string | undefined) => {
  return useQuery({
    queryKey: ["admin", "merchant-settings", merchantId],
    queryFn: async () => {
      if (!merchantId) throw new Error("Merchant ID is required");
      const res = await client.merchant[":merchantId"].settings.$get({ param: { merchantId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!merchantId,
  });
};

export type SaveMerchantSettingsPayload = {
  merchantId: string;
  productName?: string | null;
  productDescription?: string | null;
  domainKnowledge?: Record<string, string>;
};

// PUT /merchant/:merchantId/settings
export const useSaveMerchantSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ merchantId, ...payload }: SaveMerchantSettingsPayload) => {
      const res = await client.merchant[":merchantId"].settings.$put({
        param: { merchantId },
        json: payload,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "merchant-settings", merchantId] });
    },
  });
};
